/**
 * Группировка встреч журнала по календарной дате встречи (YYYY-MM-DD).
 * Часовой пояс тот же, что у formatMeetingDate: день берётся из строки даты без сдвига.
 */

export type MeetingMonthGroupKey = string; // `YYYY-MM` или `__none__`

export type MeetingForMonthGroup = {
  meetingDate: string | null;
};

export type MeetingMonthGroup<T extends MeetingForMonthGroup> = {
  key: MeetingMonthGroupKey;
  label: string;
  items: T[];
};

const MONTH_NAMES_RU = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
] as const;

const NONE_KEY = '__none__';

export function formatMeetingMonthLabel(year: number, monthIndex0: number): string {
  const name = MONTH_NAMES_RU[monthIndex0] ?? 'Месяц';
  return `${name} ${year}`;
}

export function meetingMonthGroupKey(meetingDate: string | null | undefined): MeetingMonthGroupKey {
  if (meetingDate === null || meetingDate === undefined || meetingDate.trim() === '') {
    return NONE_KEY;
  }
  const match = /^(\d{4})-(\d{2})-\d{2}$/u.exec(meetingDate.trim());
  if (!match) {
    return NONE_KEY;
  }
  return `${match[1]}-${match[2]}`;
}

export function meetingMonthGroupLabel(key: MeetingMonthGroupKey): string {
  if (key === NONE_KEY) {
    return 'Дата не указана';
  }
  const match = /^(\d{4})-(\d{2})$/u.exec(key);
  if (!match) {
    return 'Дата не указана';
  }
  const year = Number(match[1]);
  const monthIndex0 = Number(match[2]) - 1;
  return formatMeetingMonthLabel(year, monthIndex0);
}

/**
 * Группирует уже отфильтрованную/отсортированную страницу.
 * Порядок групп и записей сохраняется (API отдаёт от новых к старым, nulls last).
 */
export function groupMeetingsByMonth<T extends MeetingForMonthGroup>(
  items: readonly T[],
): MeetingMonthGroup<T>[] {
  const groups: MeetingMonthGroup<T>[] = [];
  let current: MeetingMonthGroup<T> | null = null;

  for (const item of items) {
    const key = meetingMonthGroupKey(item.meetingDate);
    if (current === null || current.key !== key) {
      current = { key, label: meetingMonthGroupLabel(key), items: [] };
      groups.push(current);
    }
    current.items.push(item);
  }

  return groups;
}
